import { keepPreviousSearchData } from "@/lib/data/react-query/search/scope";
import { useMatterPersonRoles } from "@/hooks/use-matter-person-roles";
import { ContactRowContextMenu } from "@/components/features/contact/ContactRowContextMenu";
import {
  ActionEmptyState,
  Badge,
  Button,
  Checkbox,
  DataTable,
  DraggableCardDataTable,
  LoadingSpinner,
  Typography,
  type DataTableChrome,
} from "@/components/ui";
import {
  searchContactsInfiniteQueryOptions,
  searchContactsQueryOptions,
} from "@/lib/data/react-query";
import {
  getSearchContactsCount,
  getSearchContactsNextPageNumber,
} from "@/lib/data/requests";
import { type tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { type Contact } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import {
  formatAddress,
  formatLocalOrIntlPhoneNumber,
} from "@/lib/utils/format";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  type PaginationState,
  type Row,
  type SortingState,
} from "@tanstack/react-table";
import { Building2, User } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { getContactsTableColumns } from "./contacts-table-columns";

type ContactListSubtitle =
  | { kind: "pair"; phone: string; email: string }
  | { kind: "single"; text: string }
  | { kind: "none" };

function contactListRowSubtitleParts(row: Contact): ContactListSubtitle {
  const firstPhone = Array.isArray(row.phones) ? row.phones[0] : undefined;
  const firstEmail = Array.isArray(row.emails)
    ? row.emails[0]?.address
    : undefined;
  const phoneLabel = firstPhone?.number
    ? formatLocalOrIntlPhoneNumber(firstPhone) || firstPhone.number
    : "";
  if (phoneLabel && firstEmail) {
    return { kind: "pair", phone: phoneLabel, email: firstEmail };
  }
  if (phoneLabel) {
    return { kind: "single", text: phoneLabel };
  }
  if (firstEmail) {
    return { kind: "single", text: firstEmail };
  }
  return { kind: "none" };
}

function contactListRowPrimaryAddress(row: Contact): string {
  const addresses = Array.isArray(row.addresses) ? row.addresses : [];
  const address =
    addresses.find((candidate) => candidate.isPrimary) ?? addresses.at(0);

  return address ? formatAddress(address) : "";
}

function isOrganizationContactRow(row: Contact): boolean {
  const t = row.type?.toLowerCase() ?? "";
  return t.includes("organization") || t.includes("group");
}

function contactListRowTestId(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

  return `contact-picker-option-${slug || "unnamed"}`;
}

function asContactRow(original: Contact): Row<Contact> {
  return { original } as Row<Contact>;
}

const CONTACT_SEARCH_SORT_FIELDS: Record<string, string> = {
  Birthday: "birthday",
  "Date Created": "dateCreated",
  "Date Modified": "dateModified",
  Deceased: "deceased",
  Gender: "gender",
  Language: "language",
  Name: "name",
  Race: "race",
  "Sub Type": "subType",
  Type: "type",
};

export interface ContactsTableProps {
  chrome?: DataTableChrome;
  entityLabelPlural?: string;
  searchBar?: React.ReactNode;
  tableButtons?: React.ReactNode;
  searchObject: { [key: string]: string[] };
  onRowClick: (row: Row<Contact>) => void;
  tableName: (typeof tanstackTableNames)[keyof typeof tanstackTableNames];
  generalSearch?: string;
  excludeContactIds?: string[];
  matterIds?: string[];
  /** Matter context for role labels, independent of the connected-only filter. */
  contextMatterId?: string;
  /** Picker-style list (e.g. Select Contact modal); default keeps the data table. */
  layout?: "table" | "list";
  /** Whether the list owns scrolling or inherits it from a parent modal/pane. */
  scrollContainer?: "internal" | "parent";
  /** Shows a checkbox beside each list row so callers can collect multiple contacts. */
  multi?: boolean;
  isContactSelected?: (contact: Contact) => boolean;
  onRowCountChange?: (count: number | undefined) => void;
  /** Clears picker search and filters when an active filter returns no contacts. */
  onClearFilters?: () => void;
  /** Constrains the table body to its parent and enables internal vertical scrolling. */
  scrollableBody?: boolean;
}

export const ContactsTable = ({
  chrome = "default",
  entityLabelPlural = "contacts",
  searchBar,
  tableButtons,
  searchObject,
  onRowClick,
  tableName,
  generalSearch,
  excludeContactIds,
  matterIds,
  contextMatterId,
  layout = "table",
  scrollContainer = "internal",
  multi = false,
  isContactSelected,
  onRowCountChange,
  onClearFilters,
  scrollableBody = false,
}: ContactsTableProps) => {
  const { contactRoles } = useMatterPersonRoles(contextMatterId);
  const [pagination, setPagination] = useState<PaginationState>(() => ({
    pageIndex: 0,
    pageSize: layout === "list" ? 100 : 10,
  }));
  const [sorting, setSorting] = useState<SortingState>([
    { id: "Date Created", desc: true },
  ]);
  const listScrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreContactsRef = useRef<HTMLLIElement>(null);
  const contactsTableColumns = useMemo(() => getContactsTableColumns(), []);
  const contactsTableColumnsPlainHeaders = useMemo(
    () => getContactsTableColumns({ columnHeaders: "plain" }),
    [],
  );
  const contactSearchObject = useMemo(
    () =>
      generalSearch?.trim()
        ? { ...searchObject, search: [generalSearch.trim()] }
        : searchObject,
    [generalSearch, searchObject],
  );
  const searchSignature = useMemo(
    () =>
      JSON.stringify({
        excludeContactIds,
        matterIds,
        searchObject: contactSearchObject,
      }),
    [contactSearchObject, excludeContactIds, matterIds],
  );
  const sortInput = useMemo(() => {
    const activeSort = sorting.at(0);
    const sort = activeSort && CONTACT_SEARCH_SORT_FIELDS[activeSort.id];

    return sort
      ? { order: activeSort.desc ? ("desc" as const) : ("asc" as const), sort }
      : {};
  }, [sorting]);

  useEffect(() => {
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  }, [searchSignature]);

  const contactsQuery = useQuery({
    ...searchContactsQueryOptions(contactSearchObject, {
      excludeContactIds,
      matterIds,
      ...sortInput,
      pageNumber: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    enabled: layout !== "list",
    placeholderData: keepPreviousSearchData,
  });
  const {
    data: contactsInfiniteData,
    fetchNextPage,
    hasNextPage: hasNextContactsPage,
    isError: isContactsInfiniteError,
    isFetching: isContactsInfiniteFetching,
    isFetchingNextPage: isFetchingNextContactsPage,
    isLoading: isContactsInfiniteLoading,
  } = useInfiniteQuery({
    ...searchContactsInfiniteQueryOptions(contactSearchObject, {
      excludeContactIds,
      matterIds,
      ...sortInput,
      pageSize: 10,
    }),
    enabled: layout === "list",
  });
  const contactsData = useMemo(() => {
    if (layout !== "list") {
      return contactsQuery.data;
    }

    const pages = contactsInfiniteData?.pages ?? [];
    const latestPage = pages.at(-1);
    return latestPage
      ? { ...latestPage, data: pages.flatMap((page) => page.data) }
      : undefined;
  }, [contactsInfiniteData?.pages, contactsQuery.data, layout]);
  const isContactsLoading =
    layout === "list" ? isContactsInfiniteLoading : contactsQuery.isLoading;
  const isContactsFetching =
    layout === "list" ? isContactsInfiniteFetching : contactsQuery.isFetching;
  const isContactsError =
    layout === "list" ? isContactsInfiniteError : contactsQuery.isError;

  useEffect(() => {
    if (
      layout !== "list" ||
      !hasNextContactsPage ||
      isFetchingNextContactsPage
    ) {
      return;
    }

    const sentinel = loadMoreContactsRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          void fetchNextPage();
        }
      },
      {
        root:
          scrollContainer === "internal"
            ? listScrollContainerRef.current
            : null,
        rootMargin: "160px",
      },
    );
    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [
    fetchNextPage,
    hasNextContactsPage,
    isFetchingNextContactsPage,
    layout,
    scrollContainer,
  ]);
  const nextPageNumber = getSearchContactsNextPageNumber(contactsData);
  const stableRowCount = getSearchContactsCount(contactsData);

  useEffect(() => {
    onRowCountChange?.(stableRowCount);
  }, [onRowCountChange, stableRowCount]);
  const pageCount =
    typeof stableRowCount === "number"
      ? Math.max(1, Math.ceil(stableRowCount / pagination.pageSize))
      : Math.max(
          1,
          nextPageNumber ? pagination.pageIndex + 2 : pagination.pageIndex + 1,
        );

  const filteredContacts = useMemo(() => {
    return contactsData?.data ?? [];
  }, [contactsData?.data]);
  const hasActiveFilters =
    Boolean(generalSearch?.trim()) ||
    Boolean(matterIds?.length) ||
    Object.values(searchObject).some((values) => values.length > 0);

  const sortedContactsForList = useMemo(() => {
    return [...filteredContacts].sort((a, b) => {
      const da = a.dateCreated ?? "";
      const db = b.dateCreated ?? "";
      return db.localeCompare(da);
    });
  }, [filteredContacts]);

  if (layout === "list") {
    // Only full-area spinner on initial load; background refetches keep the list visible.
    const loading = isContactsLoading;
    const usesInternalScroll = scrollContainer === "internal";
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
        {usesInternalScroll && searchBar ? (
          <div className="bg-card shrink-0 px-1 pt-1 pb-4">{searchBar}</div>
        ) : null}
        <div
          ref={usesInternalScroll ? listScrollContainerRef : undefined}
          className={
            usesInternalScroll
              ? "min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1"
              : "pr-1"
          }
        >
          <div
            className={
              usesInternalScroll
                ? "flex h-full min-h-full flex-col gap-4"
                : "flex flex-col gap-4"
            }
          >
            {!usesInternalScroll && searchBar ? (
              <div className="bg-card sticky top-0 z-[1] px-1 pt-1">
                {searchBar}
              </div>
            ) : null}
            {loading ? (
              <div className="flex h-full min-h-[200px] items-center justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : isContactsError ? (
              <div className="flex h-full min-h-[200px] items-center justify-center">
                <Typography variant="subHeading" className="text-destructive">
                  Could not load contacts.
                </Typography>
              </div>
            ) : sortedContactsForList.length === 0 ? (
              <div className="flex h-full min-h-[200px] items-center justify-center">
                {hasActiveFilters && onClearFilters ? (
                  <ActionEmptyState
                    variant="plain"
                    title="No contacts match your search."
                    description="Try adjusting or clearing your search and filters."
                    actions={
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={onClearFilters}
                      >
                        Clear filters
                      </Button>
                    }
                  />
                ) : (
                  <Typography
                    variant="subHeading"
                    className="text-muted-foreground py-10 text-center"
                  >
                    No contacts match your search.
                  </Typography>
                )}
              </div>
            ) : (
              <ul className="space-y-1">
                {sortedContactsForList.map((contact, index) => {
                  const id = contact.id || `contact-${index}`;
                  const name =
                    [
                      contact.individualFirstName,
                      contact.individualLastName,
                      contact.organizationName,
                      contact.groupName,
                    ]
                      .filter(Boolean)
                      .join(" ") || "Unnamed";
                  const subtitleParts = contactListRowSubtitleParts(contact);
                  const primaryAddress = contactListRowPrimaryAddress(contact);
                  const OrgIcon = isOrganizationContactRow(contact)
                    ? Building2
                    : User;
                  const isSelected = isContactSelected?.(contact) ?? false;
                  return (
                    <li key={id}>
                      <div
                        className="hover:bg-muted/60 group flex w-full cursor-pointer items-center gap-3 rounded-lg px-2 py-3 text-left transition-colors"
                        data-testid={contactListRowTestId(name)}
                        onClick={() => {
                          onRowClick(asContactRow(contact));
                        }}
                      >
                        {multi ? (
                          <span
                            className={cn(
                              "transition-opacity",
                              isSelected
                                ? "opacity-100"
                                : "opacity-0 group-focus-within:opacity-100 group-hover:opacity-100",
                            )}
                          >
                            <Checkbox
                              checked={isSelected}
                              aria-label={`Select ${name}`}
                              onClick={(event) => {
                                event.stopPropagation();
                              }}
                              onCheckedChange={() => {
                                onRowClick(asContactRow(contact));
                              }}
                            />
                          </span>
                        ) : null}
                        <div className="bg-muted text-muted-foreground flex size-10 shrink-0 items-center justify-center rounded-md border">
                          <OrgIcon className="size-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <p className="text-foreground truncate text-sm font-semibold">
                              {name}
                            </p>
                            {contactRoles.get(contact.id)?.map((role) => (
                              <Badge
                                key={role}
                                variant="secondary"
                                className="font-normal"
                              >
                                {role}
                              </Badge>
                            ))}
                          </div>
                          {subtitleParts.kind === "pair" ? (
                            <p className="text-muted-foreground mt-0.5 truncate text-sm">
                              <span>{subtitleParts.phone}</span>
                              <span
                                className="text-foreground/45 mx-1.5 font-medium select-none"
                                aria-hidden
                              >
                                |
                              </span>
                              <span>{subtitleParts.email}</span>
                            </p>
                          ) : subtitleParts.kind === "single" ? (
                            <p className="text-muted-foreground mt-0.5 truncate text-sm">
                              {subtitleParts.text}
                            </p>
                          ) : null}
                          {primaryAddress ? (
                            <p className="text-muted-foreground mt-0.5 truncate text-sm">
                              {primaryAddress}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  );
                })}
                {hasNextContactsPage ? (
                  <li
                    ref={loadMoreContactsRef}
                    className="flex min-h-12 items-center justify-center py-2"
                  >
                    {isFetchingNextContactsPage ? <LoadingSpinner /> : null}
                  </li>
                ) : null}
              </ul>
            )}
          </div>
        </div>
        {tableButtons ? (
          <div
            className={cn(
              "border-border bg-card flex w-full shrink-0 flex-col gap-2 border-t px-1 pt-2 pb-1 [&_button]:w-full [&_button]:justify-center",
              !usesInternalScroll &&
                "bg-card/95 sticky bottom-0 z-[1] backdrop-blur-sm",
            )}
          >
            {tableButtons}
          </div>
        ) : null}
      </div>
    );
  }

  if (chrome === "matterDashboard") {
    return (
      <DraggableCardDataTable
        searchBar={searchBar}
        tableButtons={tableButtons}
        columns={contactsTableColumnsPlainHeaders}
        data={filteredContacts}
        isLoading={isContactsLoading}
        isFetching={isContactsFetching}
        isError={isContactsError}
        onRowClick={onRowClick}
        renderContextMenu={(row, trigger) => (
          <ContactRowContextMenu contact={row.original} trigger={trigger} />
        )}
        tableName={tableName}
        entityLabelPlural={entityLabelPlural}
        fixedColumnWidths
        initialSorting={[{ id: "Date Created", desc: true }]}
        initialVisibility={{
          "Date Created": false,
          "Date Modified": false,
          Gender: false,
          Language: false,
          Race: false,
          Deceased: false,
          Tags: false,
          Birthday: false,
          "Sub Type": false,
        }}
        serverPagination={{
          pagination,
          onPaginationChange: setPagination,
          pageCount,
          rowCount: stableRowCount,
          getCanNextPage: Boolean(nextPageNumber),
        }}
        serverSorting={{ sorting, onSortingChange: setSorting }}
        scrollableBody={scrollableBody}
      />
    );
  }

  return (
    <DataTable
      chrome={chrome}
      entityLabelPlural={entityLabelPlural}
      hideSearchBar
      leftToolbarContent={searchBar}
      tableButtons={tableButtons}
      columns={contactsTableColumns}
      initialSort={{ id: "Date Created", desc: true }}
      data={filteredContacts}
      onRowClick={(row) => {
        onRowClick(row);
      }}
      isLoading={isContactsLoading}
      isFetching={isContactsFetching}
      isError={isContactsError}
      tableName={tableName}
      initialVisibility={{
        "Date Created": false,
        "Date Modified": false,
        Gender: false,
        Language: false,
        Race: false,
        Deceased: false,
        Tags: false,
        Birthday: false,
        "Sub Type": false,
      }}
      serverPagination={{
        pagination,
        onPaginationChange: setPagination,
        pageCount,
        rowCount: stableRowCount,
        getCanNextPage: Boolean(nextPageNumber),
      }}
      serverSorting={{ sorting, onSortingChange: setSorting }}
    />
  );
};
